import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import { listPointState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import {
  Annotation,
  FloorMapElement,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import { Direction, KrpanoInitProps, Stairway } from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'

export default function useViewerKrpano() {
  const { id } = useParams()
  const { t } = useTranslation()
  const [points] = useRecoilState(listPointState)
  const [searchParams] = useSearchParams()
  const urlH = searchParams.get('hlookat')
  const urlV = searchParams.get('vlookat')
  const urlZ = searchParams.get('fov')
  const isFirstLoading = useRef<boolean>(true)
  const isAnnotationDisplay = searchParams.get('is_display')

  // Mobile FOV multiplier used for tour viewer
  const MOBILE_FOV_FACTOR = 1.6
  // Default FOV value set by the backend – only apply mobile factor when FOV equals this
  const DEFAULT_FOV = 100

  // Client-only FOV adjust helper (uses window.innerWidth so safe on reload).
  // Only multiplies MOBILE_FOV_FACTOR when FOV is the default value (100).
  // If user/admin has set a custom FOV, keep it unchanged.
  const adjustFovForMobileClient = (z: number | string) => {
    const zNum = Number(z)
    if (Number.isNaN(zNum)) return zNum
    const isClient =
      typeof window !== 'undefined' && typeof window.innerWidth === 'number'
    const isMobile = isClient ? window.innerWidth < 768 : false
    return isMobile && zNum === DEFAULT_FOV ? zNum * MOBILE_FOV_FACTOR : zNum
  }

  const { navigate, getBuiildingType, getDirectory, directory } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)

  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [povPerPoint, setPovPerPoint] = useRecoilState(povPerPointState)

  const [currentHgap, setCurrentHgap] = useState(0)

  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'

  // function get all data for create scene
  const { trigger: getSenceData } = useMutation(`${apiPrefix}/points/file-info`)

  const { data: building } = useSWR<Building>({
    url: isCommunity
      ? `${apiPrefix}/buildings/${id}`
      : `${apiPrefix}/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
  })
  const { data: mapItems, isValidating: isValidatingMapItems } =
    useSWR<FloorMapElement>(
      currentViewer.selectedFloor
        ? {
            url: `${apiPrefix}/points`,
            params: {
              buildingId: id,
              floorId: currentViewer.selectedFloor
            }
          }
        : null
    )
  const { trigger, isMutating: isMutatingAnnotation } = useMutation(
    `${apiPrefix}/annotations`
  )
  // handle redirect to next point
  useEffect(() => {
    const handleRedirect = (item: any, clickh: any, clickv: any) => {
      const ponitSelected = mapItems?.points?.find((point) => point.id === item)
      if (ponitSelected?.image360) {
        const krpano = document.getElementById('embedpano-viewer') as any
        Krpano.LoadCheckingViewerMode(() => {
          const h = krpano?.get('view.hlookat')
          const v = krpano?.get('view.vlookat')
          const z = krpano?.get('view.fov')

          const urlToUpdate = new URL(window.location.href)
          urlToUpdate.searchParams.set('hlookat', h)
          urlToUpdate.searchParams.set('vlookat', v)
          urlToUpdate.searchParams.set('fov', z)
          // Clear subimage when navigating to a different point via direction arrow
          urlToUpdate.searchParams.delete('subimage')

          // statis fpr display all anotation or hidden all anotation
          const isDisplay = urlToUpdate.searchParams.get('is_display')

          if (isDisplay) {
            urlToUpdate.searchParams.set('is_display', isDisplay)
          }

          window.history.pushState({}, '', urlToUpdate.toString())

          // Step 1: lock React/SWR flow BEFORE Krpano starts loading
          setCurrentViewer((prev) => ({
            ...prev,
            isClickToDirection: true,
            isSelectPointInLeftMenu: false,
            isClickFromTourCarousel: false
          }))

          const baseH = Number(h)
          const currentHgapNum = Number(currentHgap) || 0
          const targetRadarHGap = Number(ponitSelected.initView?.radarHGap) || 0

          // [CONTINUITY] Prepare POV for arrow navigation:
          // Adjust by current point hgap vs target point radarHGap to maintain visual direction.
          const adjustedZRedirect = adjustFovForMobileClient(z)
          const currentPovData = {
            h: baseH,
            v: Number(v),
            z: adjustedZRedirect
          }
          setCurrentPov(currentPovData)
          // V2 (Approach A): Krpano is the single source-of-truth for arrow navigation.
          // Include + load the target scene here to avoid double-load/snap from React.
          krpano.call(`
            set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, ${
              currentHgapNum - targetRadarHGap
            }, 0.0, 0.9, easeoutquad);
          `)

          try {
            const targetImageId = ponitSelected.image360?.id
            if (!targetImageId) return

            const sceneName = Krpano.getScenceNameByFileId(
              ponitSelected.id,
              points,
              targetImageId
            )

            if (krpano.get(`scene[${sceneName}]`) === null) {
              const screenString = Krpano.generateSceneXML({
                sceneName,
                tilesUrl:
                  (ponitSelected.image360 as any)?.tilesUrl || undefined,
                multires:
                  (ponitSelected.image360 as any)?.multires || undefined,
                s3Path: (ponitSelected.image360 as any)?.s3Path || '',
                thumbnailUrl: (ponitSelected.image360 as any)?.multires
                  ? `${(ponitSelected.image360 as any)?.tilesUrl}/preview.jpg`
                  : (ponitSelected.image360 as any)?.thumbnail || ''
              })

              krpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }

            const adjustedZToLoad = adjustFovForMobileClient(currentPovData.z)
            krpano.call(
              `load_scene(${sceneName},${currentPovData.h},${currentPovData.v},${adjustedZToLoad})`
            )

            // Step 4: delay React state (selectedPoint/selectedImage) to avoid racing Krpano blend
            const blendTimeMs = 500
            setTimeout(() => {
              setCurrentViewer((prev) => ({
                ...prev,
                selectedImage: targetImageId,
                selectedPoint: ponitSelected.id,
                selectedArea: `${ponitSelected.areaId}`,
                // keep lock flags true; SWR onSuccess will reset
                isClickToDirection: true,
                isClickToNextFloor: true,
                isSelectPointInLeftMenu: false,
                isClickFromTourCarousel: false
              }))
            }, blendTimeMs)
          } catch (e) {}
        })
      } else {
        notification.error({
          key: 'redirect',
          message: '画像が設定されていません'
        })
      }
    }

    // Only set the function if it doesn't exist or if dependencies changed
    const depsKey = `${mapItems?.points?.length}-${currentHgap}`
    if (
      !(window as any).handle_redirect ||
      (window as any).handle_redirect.depsKey !== depsKey
    ) {
      ;(window as any).handle_redirect = handleRedirect
      ;(window as any).handle_redirect.depsKey = depsKey
    }

    return () => {
      // Only cleanup if component unmounts, not on every re-render
      if ((window as any).handle_redirect?.depsKey === depsKey) {
        delete (window as any).handle_redirect
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapItems?.points?.length,
    currentHgap,
    isValidatingMapItems,
    points,
    setCurrentViewer,
    setCurrentPov,
    adjustFovForMobileClient
  ])

  // handle click annotaion
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annotationId: string) => {
      !isMutatingAnnotation &&
        setCurrentSelected((prev) => ({
          ...prev,
          isMutating: true
        }))
      trigger(
        {
          method: 'get',
          data: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor,
            point_id: currentViewer?.selectedPoint
          },
          query: [annotationId]
        },
        {
          onSuccess: (data) => {
            setCurrentSelected({ ...data, isMutating: false })
          },
          onError: (error) => {
            setCurrentSelected((prev) => ({
              ...prev,
              isMutating: false
            }))
            if (error.response.data.error.key === 'annotation_not_exists') {
              const krpano = document.getElementById('embedpano-viewer') as any
              krpano.call(`remove_annotation(${annotationId})`)
              notification.error({
                message: t('translation.deletedAnnotationNotice')
              })
            }
            Utils.handleErrorNavigate(error, navigate)
          }
        }
      )
    }
  }

  const handleChangeFloor = () => {
    ;(window as any).handle_change_view_floor = (
      stair_id: string,
      targetFloor: string,
      targetPoint: string,
      clickh: any,
      clickv: any,
      areaId: string
    ) => {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (!building?.floors.find((item) => item.id === Number(targetFloor))) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stair_id}')`)
        return
      }

      const h = embedpano?.get('view.hlookat')
      const v = embedpano?.get('view.vlookat')
      const z = embedpano?.get('view.fov')

      const urlToUpdate = new URL(window.location.href)
      urlToUpdate.searchParams.set('floor', targetFloor)
      urlToUpdate.searchParams.set('point', targetPoint)
      if (areaId) {
        urlToUpdate.searchParams.set('area', areaId)
      } else {
        urlToUpdate.searchParams.delete('area')
      }
      urlToUpdate.searchParams.set('hlookat', h)
      urlToUpdate.searchParams.set('vlookat', v)
      urlToUpdate.searchParams.set('fov', z)
      urlToUpdate.searchParams.delete('subimage')
      urlToUpdate.searchParams.set('is_click_to_next_floor', 'true')
      urlToUpdate.searchParams.delete('subimage')

      window.location.href = urlToUpdate.toString()
    }
  }

  const addPropertiesToKrpano = useCallback(
    (
      krpano: any,
      annotations?: Annotation[],
      directions?: Direction[],
      stairways?: Stairway[],
      tripodUrl?: string
    ) => {
      annotations &&
        Krpano.AddAnnotation(
          krpano,
          annotations,
          isAnnotationDisplay,
          getDirectory() === 'tour',
          false
        )
      // add tripod logo
      Krpano.AddTripodLogo(krpano, tripodUrl)

      directions && Krpano.AddDirectionArrow(krpano, directions)

      stairways && Krpano.AddStairway(krpano, stairways)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAnnotationDisplay]
  )

  const krpanoOnreadyCallback = async (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string
  ) => {
    const { h, v, z, previewUrl, tilesUrl, multires, imageUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true}`
    krpano.call(
      `loadpano('/krpano/xml-assets/pano_view.xml?v=1.0.1','${varString}',IGNOREKEEP,BLEND(1.0));`
    )
    const sceneName = Krpano.getScenceNameByFileId(
      currentViewer?.selectedPoint,
      points,
      undefined
    )

    const sceneString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })
    krpano.call(`includexmlstring(${sceneString}, null, null, false)`)

    // mark krpano loading started
    ;(window as any).setKrpanoLoadingStatus?.(true)
    setCurrentViewer((prev) => ({ ...prev, imageIsLoading: true }))
    const adjustedZ = adjustFovForMobileClient(z)
    krpano.call(`load_scene(${sceneName},${h},${v},${adjustedZ})`)
    // enforce adjusted FOV immediately after load to avoid timing/hydration issues
    try {
      krpano.call(`lookto(${h},${v},${adjustedZ}, 0);`)
      // call again after a short delay in case load_scene overrides view during tile load
      setTimeout(() => {
        try {
          krpano.call(`lookto(${h},${v},${adjustedZ}, 0);`)
        } catch (e) {}
      }, 150)
    } catch (e) {}
    // stronger enforcement: directly set view variables after a small delay
    setTimeout(() => {
      try {
        krpano.call(
          `set(view.hlookat, ${h}); set(view.vlookat, ${v}); set(view.fov, ${adjustedZ});`
        )
      } catch (e) {}
    }, 350)

    setTimeout(async () => {
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        try {
          await document.fonts.ready
        } catch {
          // continue
        }
      }

      addPropertiesToKrpano(
        krpano,
        annotations,
        directions,
        stairways,
        tripodUrl
      )
      // mark as loaded after properties applied and view settled
      setTimeout(() => {
        ;(window as any).setKrpanoLoadingStatus?.(false)
        setCurrentViewer((prev) => ({ ...prev, imageIsLoading: false }))
      }, 500)
    }, 100)

    isFirstLoading.current = false
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string,
    pointDetail?: Point
  ) => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const target = document.getElementById('viewer-pano-container')
    let currentSelectedPoint = currentViewer?.selectedPoint
    let currentSelectedImage = currentViewer.selectedImage
    if (pointDetail) {
      currentSelectedImage = pointDetail.image360?.id
      currentSelectedPoint = pointDetail.id
    }
    const { imageUrl, previewUrl, multires, tilesUrl, h, v, z } =
      krpanoInitProps
    if (target) {
      // fist time only whne krpano not mount -> init krpano with current point and init properties
      if (!embedpano) {
        ;(window as any).embedpano({
          id: 'embedpano-viewer',
          xml: null,
          target: 'viewer-pano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directions,
              stairways,
              tripodUrl
            )
          }
        })
      } else {
        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: pointDetail?.areaId?.toString()
        }))
        // call when krpano mounted and change point or click to direction
        const sceneName = Krpano.getScenceNameByFileId(
          currentSelectedPoint,
          points,
          currentSelectedImage
        )

        if (embedpano.get(`scene[${sceneName}]`) === null) {
          const sceneString = Krpano.generateSceneXML({
            sceneName,
            tilesUrl: tilesUrl || undefined,
            multires: multires || undefined,
            s3Path: imageUrl,
            thumbnailUrl: previewUrl || ''
          })
          embedpano.call(`includexmlstring(${sceneString}, null, null, false)`)
        }

        if (currentViewer.isClickToDirection) {
          // V2 (Approach A): Do NOT call load_scene here.
          // Arrow navigation already included + loaded the target scene in handle_redirect.
          // We only (re)add hotspots/annotations after data is available.
          setTimeout(() => {
            try {
              addPropertiesToKrpano(
                embedpano,
                annotations,
                directions,
                stairways,
                tripodUrl
              )
            } catch (e) {}
          }, 200)
        } else {
          // load init view
          // Set loading true before changing scene
          ;(window as any).setKrpanoLoadingStatus?.(true)
          setCurrentViewer((prev) => ({ ...prev, imageIsLoading: true }))
          const adjustedZEmbed = adjustFovForMobileClient(z)
          embedpano.call(`load_scene(${sceneName},${h},${v},${adjustedZEmbed})`)
          // enforce adjusted FOV immediately after load (no animation)
          try {
            embedpano.call(`lookto(${h},${v},${adjustedZEmbed}, 0);`)
            setTimeout(() => {
              try {
                embedpano.call(`lookto(${h},${v},${adjustedZEmbed}, 0);`)
              } catch (e) {}
            }, 150)
          } catch (e) {}
          // stronger enforcement for embedpano as well
          setTimeout(() => {
            try {
              embedpano.call(
                `set(view.hlookat, ${h}); set(view.vlookat, ${v}); set(view.fov, ${adjustedZEmbed});`
              )
            } catch (e) {}
          }, 350)

          addPropertiesToKrpano(
            embedpano,
            annotations,
            directions,
            stairways,
            tripodUrl
          )
          // mark loaded after small delay to allow view to settle
          setTimeout(() => {
            ;(window as any).setKrpanoLoadingStatus?.(false)
            setCurrentViewer((prev) => ({ ...prev, imageIsLoading: false }))
          }, 500)
        }
      }
    }
  }

  const handlePreloadScenes = async () => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const listPointTo = points
      .find((item) => item.id === currentViewer?.selectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(currentViewer.selectedFloor),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentViewer?.selectedPoint
            )?.image360?.id

            const items: SenceData[] = []

            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  // get point detail when select point
  // Ensure selectedPoint belongs to current floor's points to avoid 404
  // when floor changes but selectedPoint hasn't been updated yet
  const isPointInCurrentFloor =
    currentViewer?.selectedPoint &&
    mapItems?.points?.some((p) => p.id === currentViewer.selectedPoint)
  const { error: pointDetailError } = useSWR<Point>(
    currentViewer.selectedFloor &&
      currentViewer?.selectedPoint &&
      building &&
      mapItems &&
      isPointInCurrentFloor
      ? {
          url: `${apiPrefix}/points/${currentViewer?.selectedPoint}`,
          params: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      onSuccess: (data) => {
        const urlParams = new URL(window.location.href).searchParams
        const currentUrlSubimage = urlParams.get('subimage')
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const selectedImageId =
          (currentUrlSubimage
            ? +currentUrlSubimage
            : currentViewer.selectedImage) || data.image360?.id
        const selectedImageData =
          data.subImages?.find((img) => img.id === selectedImageId) ||
          data.image360

        // update param  area & subimage
        const urlToUpdate = new URL(window.location.href)
        const floorToSet = data.floorId || currentViewer.selectedFloor
        if (floorToSet) {
          urlToUpdate.searchParams.set('floor', floorToSet.toString())
        }
        urlToUpdate.searchParams.set('point', data.id)
        urlToUpdate.searchParams.set('area', data.areaId?.toString())
        if (
          !currentViewer.isClickToDirection &&
          !currentViewer.isClickToNextFloor &&
          !isFirstLoading.current
        ) {
          urlToUpdate.searchParams.delete('hlookat')
          urlToUpdate.searchParams.delete('vlookat')
          urlToUpdate.searchParams.delete('fov')
        }

        if (selectedImageId && selectedImageId !== data.image360?.id) {
          urlToUpdate.searchParams.set('subimage', selectedImageId.toString())
        } else {
          urlToUpdate.searchParams.delete('subimage')
        }

        window.history.pushState({}, '', urlToUpdate.toString())

        if (selectedImageData) {
          // Approach A (V2): During arrow/carousel navigation, Krpano (or carousel hook)
          // is responsible for including + loading the scene. React must NOT call embedpano/load_scene,
          // otherwise it can cause double-load and snap back to default view.
          // Floor changes require full scene reload via embedpano.
          // isClickToNextFloor means user selected a different floor,
          // so always go through embedpano route for full reload.
          const isKrpanoControlledNav =
            (!!currentViewer.isClickToDirection ||
              !!currentViewer.isClickFromTourCarousel) &&
            !currentViewer.isClickToNextFloor

          const isInternalNav =
            currentViewer.isClickToDirection || currentViewer.isClickToNextFloor

          const isSamePoint = currentViewer.pointDetail?.id === data.id
          const storedPov = povPerPoint[data.id]

          let pov
          if (isSamePoint && currentPov) {
            // [SYNC] Parent-Child Sync: Within same point (sub-images), persist current orient
            pov = currentPov
          } else if (isInternalNav && currentPov) {
            // [INHERIT] Continuity via arrow/stair
            pov = currentPov
          } else if (
            isAnnotationDisplay === 'true' &&
            storedPov &&
            !currentViewer.isSelectPointInLeftMenu
          ) {
            // v1.4: Fix POV reset when clicking Left Menu in Preview Mode
            // [MEMORY] Persistence: Restore saved POV only in Preview mode, BUT ignore if clicked from Left Menu
            pov = storedPov
          } else if (isFirstLoading.current && urlH && urlV && urlZ) {
            // [URL] Shared link entry
            pov = { h: +urlH, v: +urlV, z: +urlZ }
          } else {
            // [RESET] Different point navigation (Carousel/Map/LeftMenu) — always reset to initView
            pov = data.initView
          }

          const initProps = {
            imageUrl: selectedImageData.s3Path,
            previewUrl: selectedImageData.multires
              ? `${selectedImageData.tilesUrl}preview.jpg`
              : selectedImageData.thumbnail,
            hgap: data?.initView.radarHGap,
            h: pov.h,
            v: pov.v,
            z: urlZ && isFirstLoading.current ? +urlZ : pov.z || DEFAULT_FOV,
            multires: selectedImageData.multires,
            tilesUrl: selectedImageData.tilesUrl
          }

          setCurrentHgap(initProps.hgap)
          const annotationList = data.annotations?.filter((item) => {
            if (item.imageID) {
              return item.imageID === selectedImageData.id
            }
            return selectedImageData.id === data.image360?.id
          })

          const stairList = data.stairways?.map((item) => ({
            ...item,
            nextFloorName: building?.floors.find(
              (floor) => floor.id === +item.nextFloorId
            )?.name,
            areaId: data.areaId
          }))

          const tripodUrl = building?.extraInfo?.tripodImageUrl
          if (isKrpanoControlledNav) {
            const krpano = document.getElementById('embedpano-viewer') as any
            if (krpano) {
              try {
                addPropertiesToKrpano(
                  krpano,
                  annotationList,
                  data.directions,
                  stairList,
                  tripodUrl
                )
              } catch (e) {}
            }
            handlePreloadScenes()
          } else {
            embedpano(
              initProps,
              annotationList,
              data.directions,
              stairList,
              tripodUrl,
              data
            )
            handlePreloadScenes()
          }
        } else {
          Krpano.UnmountPanoViewer()
        }

        setCurrentViewer((prev) => ({
          ...prev,
          pointDetail: data,
          selectedPoint: data.id,
          selectedImage: selectedImageId,
          selectedArea: data.areaId?.toString(),
          isClickToDirection: false,
          isClickToNextFloor: false,
          isClickFromTourCarousel: false
        }))
      }
    }
  )

  useEffect(() => {
    if (!pointDetailError) return

    setCurrentViewer((prev) => ({
      ...prev,
      isClickToDirection: false,
      isClickToNextFloor: false,
      isClickFromTourCarousel: false,
      imageIsLoading: false
    }))

    notification.error({
      message:
        t('building.loadPointError') ||
        'ポイント情報を読み込めませんでした。ネットワーク接続を確認してください。'
    })
  }, [pointDetailError])

  // handle event view around
  handleClickAnnotation()
  handleChangeFloor()

  useEffect(() => {
    ;(window as any).sync_current_pov = (h: number, v: number, z: number) => {
      if (currentViewer.imageIsLoading) return

      setCurrentPov({ h, v, z })
      const currentId = currentViewer.pointDetail?.id
      // ONLY save to povPerPoint if NOT currently loading an image.
      // This prevents default views (0,0,0) from overwriting stored POV during scene load.
      if (currentId && !currentViewer.imageIsLoading) {
        setPovPerPoint((prev) => ({
          ...prev,
          [currentId]: { h, v, z }
        }))
      }
    }
    return () => {
      delete (window as any).sync_current_pov
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setCurrentPov,
    setPovPerPoint,
    currentViewer.pointDetail?.id,
    currentViewer.imageIsLoading
  ])

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoViewer()
    }
  }, [])

  return {
    currentViewer,
    setCurrentViewer,
    currentPov,
    setCurrentHgap,
    embedpano,
    isMutatingAnnotation
  }
}
